import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { authService, AUTH_ACTIONS } from '../services/authService';
import { clearAuthToken, getAuthToken, setAuthToken } from '../services/authStorage';
import { getApiErrorMessage } from '../utils/apiError';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [connectingAction, setConnectingAction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(() => {
    return authService
      .getMe()
      .then((profile) => {
        setUser(profile);
        return profile;
      })
      .catch(() => {
        clearAuthToken();
        setUser(null);
        return null;
      });
  }, []);

  const runAuthAction = useCallback((action, username, referralCode) => {
    setConnectingAction(action);
    setError(null);

    return authService
      .authenticate(action, username, referralCode)
      .then((result) => {
        setAuthToken(result.accessToken);
        setUser(result.user);
        return result;
      })
      .catch((err) => {
        const fallback =
          action === AUTH_ACTIONS.REGISTER
            ? 'ไม่สามารถลงทะเบียนได้'
            : 'ไม่สามารถเข้าสู่ระบบได้';
        setError(getApiErrorMessage(err, fallback));
        throw err;
      })
      .finally(() => {
        setConnectingAction(null);
      });
  }, []);

  const register = useCallback(
    (username, referralCode) =>
      runAuthAction(AUTH_ACTIONS.REGISTER, username, referralCode),
    [runAuthAction]
  );

  const login = useCallback(
    () => runAuthAction(AUTH_ACTIONS.LOGIN),
    [runAuthAction]
  );

  const disconnect = useCallback(() => {
    clearAuthToken();
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      setIsLoading(false);
      return undefined;
    }

    loadProfile().finally(() => setIsLoading(false));

    const handleLogout = () => {
      setUser(null);
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, [loadProfile]);

  const value = useMemo(
    () => ({
      user,
      isAuthenticated: Boolean(user),
      isConnecting: Boolean(connectingAction),
      connectingAction,
      isLoading,
      error,
      register,
      login,
      disconnect,
      clearError,
      loadProfile,
    }),
    [
      user,
      connectingAction,
      isLoading,
      error,
      register,
      login,
      disconnect,
      clearError,
      loadProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
